import { type FormEvent, type ReactElement, useMemo, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import { authApi } from '@/auth/api';
import { useApiErrorMessage } from '@/shared/data/errors';
import { s } from '@/theme/tokens';
import { Box, FormCard, FormSubmitButton, Inline, Section, Stack, Text } from '@/ui';
import { emailPattern, emailValidationMessage, requiredFieldMessage } from '@/ui/hooks/validation';
import { Alert, Link, TextField } from '@/ui/primitives';
import { PageShell } from '@/ui/primitives/PageShell';

type ResetPasswordPageProps = {
  session: { userId: string; orgId?: string } | null;
};

export const ResetPasswordPage = ({ session }: ResetPasswordPageProps): ReactElement => {
  const toErrorMessage = useApiErrorMessage();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const canSubmit = useMemo(() => email.trim().length > 0 && !isSubmitting, [email, isSubmitting]);

  if (session) {
    return <Navigate to="/account-settings" replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const normalisedEmail = email.trim();
    if (!normalisedEmail) {
      setEmailError(requiredFieldMessage);
      return;
    }
    if (!emailPattern.test(normalisedEmail)) {
      setEmailError(emailValidationMessage);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setEmailError(null);
    try {
      await authApi.resetPasswordRequest(normalisedEmail);
      setSuccess(true);
    } catch (error) {
      setSubmitError(toErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell maxWidth="sm" paddingX="lg" paddingY="xl" paddingBottom="xl">
      <Stack gap="lg" sx={{ pt: { xs: s('xl'), sm: s('5xl') }, gap: { xs: s('lg'), sm: s('xl') } }}>
        <Section>
          <Stack gap="lg" alignItems="center">
            <Box
              component="form"
              onSubmit={(event) => void handleSubmit(event)}
              sx={{ width: '100%' }}
            >
              <FormCard
                title="Reset Password"
                contentGap="sm"
                footer={
                  <Stack
                    direction={{ xs: 'column-reverse', sm: 'row' }}
                    gap="md"
                    sx={{
                      width: '100%',
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: 'space-between',
                    }}
                  >
                    <Inline gap="sm">
                      <Text role="body" color="textSecondary">
                        Remembered your password?
                      </Text>
                      <Link component={RouterLink} to="/sign-in" underline="hover" variant="body2">
                        Sign in
                      </Link>
                    </Inline>
                    <FormSubmitButton
                      loading={isSubmitting}
                      disabled={!canSubmit}
                      fullWidth={false}
                      fullWidthOnMobile={false}
                      type="submit"
                      sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                    >
                      Send Reset Link
                    </FormSubmitButton>
                  </Stack>
                }
              >
                {success ? (
                  <Alert
                    severity="success"
                    color="green"
                    variant="outlined"
                    sx={{
                      border: '1px solid',
                      borderColor: 'green.main',
                      color: 'green.main',
                      '& .MuiAlert-icon': { color: 'green.main' },
                    }}
                  >
                    If an account exists for that email, a reset link has been sent.
                  </Alert>
                ) : null}
                {submitError ? (
                  <Alert severity="error" color="red">
                    {submitError}
                  </Alert>
                ) : null}
                <Text role="body" color="textSecondary">
                  Enter your account email and we&apos;ll send you a link to reset your password.
                </Text>
                <TextField
                  label="Email"
                  labelGap="xs"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setEmailError(null);
                  }}
                  slotProps={{ htmlInput: { 'aria-label': 'Email' } }}
                  fullWidth
                  error={Boolean(emailError)}
                  helperText={emailError === requiredFieldMessage ? undefined : emailError}
                />
              </FormCard>
            </Box>
          </Stack>
        </Section>
      </Stack>
    </PageShell>
  );
};
